<script setup>
import { computed, onMounted, ref } from 'vue';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import NextButton from 'dashboard/components-next/button/Button.vue';
import PageHeader from 'dashboard/components-next/teletel/PageHeader.vue';

// Teletel APIs serialize ISO-8601 timestamps (dynamicTime expects unix).
const formatTime = value => (value ? new Date(value).toLocaleString() : '');

const store = useStore();
const { t } = useI18n();

// ?status=failed opens the page on failed runs (the former error log).
const statusFilter = ref(useRoute().query.status || '');
const expandedRunId = ref(null);
const events = ref([]);

const runs = computed(() => store.getters['teletelFlows/getRuns']);
const flows = computed(() => store.getters['teletelFlows/getFlows']);
const uiFlags = computed(() => store.getters['teletelFlows/getUIFlags']);

const fetchRuns = () =>
  store.dispatch(
    'teletelFlows/fetchRuns',
    statusFilter.value ? { status: statusFilter.value } : {}
  );

onMounted(() => {
  store.dispatch('teletelFlows/get');
  fetchRuns();
});

const flowName = id => flows.value.find(f => f.id === id)?.name || `#${id}`;

const toggleEvents = async run => {
  if (expandedRunId.value === run.id) {
    expandedRunId.value = null;
    return;
  }
  const payload = await store.dispatch('teletelFlows/fetchRun', run.id);
  events.value = payload.events || [];
  expandedRunId.value = run.id;
};

const stopRun = async run => {
  await store.dispatch('teletelFlows/stopRun', { runId: run.id });
  fetchRuns();
};
const resumeRun = async run => {
  await store.dispatch('teletelFlows/resumeRun', { runId: run.id });
  fetchRuns();
};
</script>

<template>
  <div class="flex w-full flex-col gap-6">
    <PageHeader :title="t('TELETEL_FLOWS.RUNS.HEADER')">
      <template #actions>
        <select
          v-model="statusFilter"
          class="w-48 mb-0 text-sm"
          @change="fetchRuns"
        >
          <option value="">—</option>
          <option
            v-for="status in [
              'running',
              'waiting',
              'paused',
              'finished',
              'stopped',
              'failed',
            ]"
            :key="status"
            :value="status"
          >
            {{ t(`TELETEL_FLOWS.STATUS.${status}`) }}
          </option>
        </select>
      </template>
    </PageHeader>

    <p v-if="!uiFlags.isFetchingRuns && !runs.length" class="text-n-slate-11">
      {{ t('TELETEL_FLOWS.RUNS.EMPTY') }}
    </p>

    <table v-if="runs.length" class="w-full text-sm divide-y divide-n-weak">
      <thead>
        <tr class="text-n-slate-11">
          <th class="py-2 text-start">
            {{ t('TELETEL_FLOWS.RUNS.TABLE.FLOW') }}
          </th>
          <th class="py-2 text-start">
            {{ t('TELETEL_FLOWS.RUNS.TABLE.CONVERSATION') }}
          </th>
          <th class="py-2 text-start">
            {{ t('TELETEL_FLOWS.RUNS.TABLE.STATUS') }}
          </th>
          <th class="py-2 text-start">
            {{ t('TELETEL_FLOWS.RUNS.TABLE.STARTED_BY') }}
          </th>
          <th class="py-2 text-start">
            {{ t('TELETEL_FLOWS.RUNS.TABLE.STARTED_AT') }}
          </th>
          <th class="py-2 text-start">
            {{ t('TELETEL_FLOWS.RUNS.TABLE.ACTIONS') }}
          </th>
        </tr>
      </thead>
      <tbody class="divide-y divide-n-weak">
        <template v-for="run in runs" :key="run.id">
          <tr class="cursor-pointer" @click="toggleEvents(run)">
            <td class="py-2 text-n-slate-12">{{ flowName(run.flow_id) }}</td>
            <td class="py-2">#{{ run.conversation_display_id }}</td>
            <td class="py-2">
              <span
                class="px-2 py-0.5 text-xs rounded-full bg-n-slate-3 text-n-slate-11"
              >
                {{ t(`TELETEL_FLOWS.STATUS.${run.status}`) }}
              </span>
            </td>
            <td class="py-2">{{ run.started_by }}</td>
            <td class="py-2">{{ formatTime(run.created_at) }}</td>
            <td class="py-2" @click.stop>
              <div class="flex gap-1">
                <NextButton
                  v-if="run.status === 'paused'"
                  teal
                  xs
                  @click="resumeRun(run)"
                >
                  {{ t('TELETEL_FLOWS.ACTIONS.RESUME') }}
                </NextButton>
                <NextButton
                  v-if="['running', 'waiting', 'paused'].includes(run.status)"
                  ruby
                  xs
                  @click="stopRun(run)"
                >
                  {{ t('TELETEL_FLOWS.ACTIONS.STOP') }}
                </NextButton>
              </div>
            </td>
          </tr>
          <tr v-if="expandedRunId === run.id">
            <td colspan="6" class="py-2 bg-n-slate-1">
              <p v-if="run.failure_reason" class="mb-1 text-xs text-n-ruby-11">
                {{ t('TELETEL_FLOWS.RUNS.FAILURE_REASON') }}:
                {{ run.failure_reason }}
              </p>
              <p class="mb-1 text-xs font-medium text-n-slate-11">
                {{ t('TELETEL_FLOWS.RUNS.EVENTS') }}
              </p>
              <ul class="flex flex-col gap-0.5 text-xs text-n-slate-11">
                <li v-for="event in events" :key="event.id">
                  {{ formatTime(event.created_at) }} · {{ event.event_type }}
                  <template v-if="event.node_id">
                    · {{ event.node_id }}
                  </template>
                  <template v-if="event.delivery_status">
                    · {{ event.delivery_status }}
                  </template>
                </li>
              </ul>
            </td>
          </tr>
        </template>
      </tbody>
    </table>
  </div>
</template>
